/**
 * Selection store (zustand). Spec §4/§6.
 *
 * On 197k rows, "the whole filtered set" carries the FILTER, not a list of ids.
 * Reused by future modules (bulk, touches).
 *
 *   mode 'ids'    -> an explicit set of selected volunteer ids.
 *   mode 'filter' -> the current filter (+ resolved count); means "all matching".
 */

import { create } from "zustand";
import type { FilterClause } from "@/api/client";

export type SelectionMode = "ids" | "filter";

export interface Selection {
  mode: SelectionMode;
  /** Present when mode === 'ids'. */
  ids?: number[];
  /** Present when mode === 'filter' (the active filter applied to the whole set). */
  filters?: FilterClause[];
  /** Number of selected entities (len(ids) or the filter count). */
  count: number;
  /**
   * Server-side random sample size for filter-mode. null = whole filtered set.
   * Set by selectRandomFromFilter; always null for selectAllByFilter / ids-mode.
   */
  sampleN: number | null;
}

export interface SelectionState extends Selection {
  /** Replace selection with an explicit id set. */
  selectIds: (ids: number[]) => void;
  /** Toggle one id in 'ids' mode. */
  toggleId: (id: number) => void;
  /** Select the whole filtered set (carries filter + count, not ids). */
  selectAllByFilter: (filters: FilterClause[], count: number) => void;
  /**
   * Select a random sample of `n` from the filtered set. Same as
   * selectAllByFilter (carries filter + count, not ids) but records sampleN=n;
   * the server samples N random ids from the full filter result at fill time.
   */
  selectRandomFromFilter: (filters: FilterClause[], count: number, n: number) => void;
  /**
   * Pick `n` random ids from `source.ids` (Fisher-Yates shuffle + slice). The
   * result is ALWAYS explicit-id mode. No network: callers resolve ids first —
   * for 'filter' via client.sampleVolunteers, for 'ids' from current selection.
   */
  selectRandomN: (
    n: number,
    source:
      | { mode: "ids"; ids: number[] }
      | { mode: "filter"; ids: number[] },
  ) => void;
  /** Clear selection. */
  clear: () => void;
}

export const useSelection = create<SelectionState>((set) => ({
  mode: "ids",
  ids: [],
  filters: undefined,
  count: 0,
  sampleN: null,

  selectIds: (ids) =>
    set(() => {
      const unique = Array.from(new Set(ids));
      return { mode: "ids", ids: unique, filters: undefined, count: unique.length, sampleN: null };
    }),

  toggleId: (id) =>
    set((state) => {
      // Toggling always drops back into explicit-id mode.
      const current = state.mode === "ids" && state.ids ? state.ids : [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { mode: "ids", ids: next, filters: undefined, count: next.length, sampleN: null };
    }),

  selectAllByFilter: (filters, count) =>
    set(() => ({ mode: "filter", ids: undefined, filters, count, sampleN: null })),

  selectRandomFromFilter: (filters, count, n) =>
    set(() => ({ mode: "filter", ids: undefined, filters, count, sampleN: n })),

  selectRandomN: (n, source) =>
    set(() => {
      // Copy then Fisher-Yates shuffle, then slice to n. Identical for both
      // sources: backend already returned <= n for 'filter', so a second
      // client-side shuffle is harmless and idempotent.
      const pool = source.ids.slice();
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      const sliced = pool.slice(0, n);
      return { mode: "ids", ids: sliced, filters: undefined, count: sliced.length, sampleN: null };
    }),

  clear: () => set(() => ({ mode: "ids", ids: [], filters: undefined, count: 0, sampleN: null })),
}));

/** Convenience hook: is a given id part of the current explicit-id selection? */
export function useIsSelected(id: number): boolean {
  return useSelection((s) => s.mode === "ids" && !!s.ids?.includes(id));
}
